import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useWsConnection } from "../../hooks/useWsConnection";
import { upsertFiltered } from "../../store/exchangeActiveSlice";
import { wsLogger } from "../../api/wsLogger";

const WS_FILTERS = ["active", "completed", "cancelled", "dispute"];

export const WsContext = createContext({
  sendRequest: () => Promise.reject(new Error("WsProvider not mounted")),
  wsConnected: false,
});

export function useWs() {
  return useContext(WsContext);
}

export default function WsProvider({ children }) {
  const dispatch = useDispatch();
  const [wsConnected, setWsConnected] = useState(false);

  const handleEvent = useCallback(
    (msg) => {
      if (msg?.type === "exchange_active_sync") {
        // Backend sends all 4 filter buckets in one push:
        // { active: [...], completed: [...], cancelled: [...], dispute: [...] }
        const data = msg?.data ?? {};
        const filterMap = {
          active:    data.active,
          completed: data.completed,
          cancelled: data.cancelled,
          dispute:   data.dispute,
        };
        Object.entries(filterMap).forEach(([filter, results]) => {
          if (Array.isArray(results)) {
            wsLogger.push(`exchange_active_sync[${filter}]`, results);
            dispatch(upsertFiltered({ filter, results }));
          }
        });
      }
    },
    [dispatch],
  );

  const handleStatus = useCallback(({ status, delay }) => {
    if (process.env.REACT_APP_NODE_ENV !== "production") {
      const extra = delay ? ` (retry in ${delay}ms)` : "";
      console.log(`[WS] ${status}${extra}`);
    }
    setWsConnected(status === "connected");
  }, []);

  const { sendRequest } = useWsConnection({ onEvent: handleEvent, onStatus: handleStatus });

  // Single global initial load — fires once when WS connects (or reconnects).
  // No other component should duplicate this on-connect fetch.
  useEffect(() => {
    if (!wsConnected) return;
    const cancelled = { value: false };
    WS_FILTERS.forEach((filter) => {
      sendRequest("transactions.list", { filter })
        .then((res) => {
          if (cancelled.value) return;
          const results = res?.data?.results ?? res?.data ?? [];
          dispatch(upsertFiltered({ filter, results }));
        })
        .catch((err) => {
          if (cancelled.value) return;
          console.error(`[WsProvider] initial load filter="${filter}":`, err);
        });
    });
    return () => { cancelled.value = true; };
  }, [wsConnected, sendRequest, dispatch]);

  return (
    <WsContext.Provider value={{ sendRequest, wsConnected }}>
      {children}
    </WsContext.Provider>
  );
}
